import { ISectionLogger } from "@multiweb/logging";
import { IconCode, IRunMethodResult } from "@multiweb/types/contracts";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { setupBem } from "Global/Dom/bem";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { IInteractive } from "UI/CommonUIInterfaces";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { convertContextFromFrameToRequest } from "../../Utilities/SharedUIMethods";
import { ControlWithLoggerParams, IHasFrameContext, IMethodControl, IStaticControl } from "../Controls/ControlInterfaces";
import { ControlWithBackground } from "../Controls/ControlWithBackground";
import { FormManager } from "../FormManager/IFormManager";
import { FrameContext } from "../UIManagerInterfaces";
import { IForm } from "./IForm";
import { ModalWindowType } from "./ModalWindow";

const DEFAULT_EVENT_SOURCE = "OW";
const DEFAULT_SUBEVENT = "*";
const className = setupBem("data-event-label");

export type DataEventLabelParams = ControlWithLoggerParams & {
    dataEventsManager: IDataEventsManager;
    ownerForm: IForm;
};

export class DataEventLabel extends ControlWithBackground implements IDisposable, IInteractive, IMethodControl, IStaticControl, IHasFrameContext {
    private readonly _logger: ISectionLogger;

    // vlastnosti z XGenu
    private _eventSource: string | null;
    private _eventName: string;
    private _subEventName: string | null;
    private _showOnMainForm: boolean = false;
    private _formName: string | null;
    private _fullMethodName: string | null;
    public frameContext: FrameContext | undefined = undefined;
    private _useMasterContext: boolean = false;

    // interní vlastnosti
    public readonly isStatic: boolean = true;
    private _methodClassName: string;
    private _methodNameWithoutClass: string;
    private _isEventRegistered: boolean = false;
    private _interactive: boolean = true;

    // interní podelementy
    private _nameElement: HTMLElement;
    private _eventValueElement: HTMLElement;
    private readonly _dataEventsManager: IDataEventsManager;
    private readonly _ownerForm: IForm;

    //#region Properties
    public get eventSource(): string {
        return this._eventSource || DEFAULT_EVENT_SOURCE;
    }

    public set eventSource(v: string) {
        this._unregisterSubscribe();
        this._eventSource = v;
        this._registerSubscribe();
    }

    public get eventName(): string {
        return this._eventName;
    }

    public set eventName(v: string) {
        this._unregisterSubscribe();
        this._eventName = v;
        this._registerSubscribe();
    }

    public get subEventName(): string {
        return this._subEventName || DEFAULT_SUBEVENT;
    }

    public set subEventName(v: string) {
        this._unregisterSubscribe();
        this._subEventName = v;
        this._registerSubscribe();
    }

    public get methodName(): string {
        return this._fullMethodName || "";
    }

    public override get locKey(): string {
        return super.locKey;
    }

    public override set locKey(v: string) {
        super.locKey = v;
        if (v && this._nameElement)
            Localization.addLocTxtData(this._nameElement, v);
    }

    public get formName(): string {
        return this._formName;
    }

    public set formName(v: string) {
        if (this._formName) {
            GenericControl.unregisterEventListener(this.element, "click");
        }
        this._formName = v;
        if (this._formName)
            GenericControl.registerEventListener(this.element, "click", async () => {
                if (!this.interactive || !await this._ownerForm.checkAndSaveForm({ forceSave: this._useMasterContext, skipSavingEditContextId: this._useMasterContext }))
                    return;

                const transactionContextGetter = this._useMasterContext ? this.frameContext.getTransactionContextId : null;

                if (this.showOnMainForm) {
                    FormManager.instance.openForm(this._formName, { formContext: this.frameContext, transactionContextGetter });
                } else {
                    FormManager.instance.openFormModal({
                        formName: this.formName,
                        parametricData: { formContext: this.frameContext, transactionContextGetter },
                        type: ModalWindowType.parametricForm,
                    });
                }
            });

        this._updateIcon();
    }

    public get showOnMainForm(): boolean {
        return this._showOnMainForm;
    }

    public set showOnMainForm(v: boolean) {
        this._showOnMainForm = v;
        this._updateIcon();
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(val: boolean) {
        this._interactive = val;
        this.element.classList.toggle(className.modifier("disabled"), !val);
    }
    //#endregion Properties

    constructor({ logger, dataEventsManager, ...otherParams }: DataEventLabelParams) {
        super(otherParams);

        this.element.classList.add(className(), "icon");
        this._dataEventsManager = dataEventsManager;
        this._logger = logger;
        this._ownerForm = otherParams.ownerForm;

        // vlastnosti z XGenu
        this._eventSource = this.getDataAttr("eventsource");
        this._eventName = this.getDataAttr("eventname");
        this._subEventName = this.getDataAttr("subeventname");
        const fullMethodName = this.getDataAttr("methodname");
        this.formName = this.getDataAttr("formname");
        this.showOnMainForm = this.getBoolDataAttr("showonmainform");
        this.locKey = this.getDataAttr("captionlockey");
        this._useMasterContext = this.getBoolDataAttr("UseMasterContext", false);

        if (!this.eventName) {
            this._logger.error("Property EventName is required! Component disabled!");
            return;
        }
        if (!fullMethodName) {
            this._logger.error("Property MethodName is required! Component disabled!");
            return;
        }

        // další validace názvu metody
        this.setFullMethodName(fullMethodName);

        this._nameElement = createElement({ classes: className.element("title") });
        this._eventValueElement = createElement({ classes: className.element("value") });

        Localization.addLocTxtData(this._nameElement, this.locKey);

        this.element.appendChild(this._nameElement);
        this.element.appendChild(this._eventValueElement);

        this._registerSubscribe();
        this._loadData();
    }

    public setFullMethodName(fullMethodName: string): void {
        const [className, methodName] = StringUtils.splitFirst(fullMethodName, ".");

        if (!className || !methodName)
            throw new Error(`MethodName property is invalid. Value must be in format 'ClassName.MethodName'. Current value: '${fullMethodName}', parsed as '${className}.${methodName}'`);

        this._fullMethodName = fullMethodName;
        this._methodClassName = className;
        this._methodNameWithoutClass = methodName;
    }

    private _updateIcon(): void {
        this.element.classList.toggle(className.modifier("with-form"), !!this._formName);
        this.element.classList.toggle(IconCode.uiArrowTopRight, this._formName && this.showOnMainForm); // při novém formu šipka doprava nahoru
        this.element.classList.toggle(IconCode.uiChevronRight, this._formName && !this.showOnMainForm); // při modálním formu šipka vpravo
    }

    private async _loadData(): Promise<void> {
        if (!this._canRunMethod())
            return;

        let result: IRunMethodResult;
        try {
            result = await OWManager.runStaticMethodWithParams({
                className: this._methodClassName,
                methodName: this._methodNameWithoutClass,
                requestContext: convertContextFromFrameToRequest(this.frameContext),
            });
        } catch (e) {
            // pokud uživatel nemá na metodu práva, tak se ni nezobrazuje
            return;
        }
        if (result.messages?.length > 0)
            this._logger.error(`Method '${this._methodNameWithoutClass}' returned ${result.messages.length} message(s). Messages aren't supported and are ignored!`);
        this._showValue(result.resultValue);
    }

    private _showValue(value: string): void {
        this._eventValueElement.textContent = value;
    }

    private _canRunMethod(): boolean {
        return this.enabled && this.visible;
    }

    private _onmsgEvent = (): void => {
        this._loadData();
    };

    private _unregisterSubscribe(): void {
        if (!this._isEventRegistered)
            return;

        this._dataEventsManager.unsubscribeDataEvent(this.eventSource, this.eventName, this.subEventName, this._onmsgEvent);
        this._isEventRegistered = false;
    }

    private _registerSubscribe(): void {
        if (this._isEventRegistered)
            this._unregisterSubscribe();

        this._dataEventsManager.subscribeDataEvent(this.eventSource, this.eventName, this.subEventName, this._onmsgEvent);
        this._isEventRegistered = true;
    }

    public override dispose(): void {
        super.dispose();

        if (this._isEventRegistered)
            this._unregisterSubscribe();
    }
}
